import { Flex, Spinner, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { customersApi } from '@texas/api/endpoints/customersApi';
import { FileLink, filesApi } from '@texas/api/endpoints/filesApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useFileUploads } from '@texas/api/hooks/useFileUploads';
import { ImageDropzone } from '@texas/components/shared/dropzone/ImageDropzone';
import { ErrorsList } from '@texas/components/shared/ErrorsList';
import { ServerError } from '@texas/types';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { maxSize } from '@texas/components/shared/dropzone/shared';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { clientEndpoints } from '@texas/clientEndpoints';
import { FileDropzone } from '@texas/components/shared/dropzone/FileDropzone';
import { acceptedImageFormats } from '@texas/utils/helpers/filesHelper';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';

export function CustomerLogo({
  customerId,
  folderId,
  fileId,
}: {
  customerId: number;
  folderId: number;
  fileId?: number;
}) {
  const { t } = useTranslation();
  const toast = useToast();

  const {
    isOpen: isRemoveFileOpen,
    onClose: onRemoveFileClose,
    onOpen: onRemoveFileOpen,
  } = useDisclosure();

  const {
    data: file,
    refetch,
    loading,
    set: setFile,
    error,
  } = useApiResource(filesApi.getFileLink);

  useEffect(() => {
    if (fileId) refetch(fileId);
  }, [refetch, fileId]);

  const { request: updateRequest } = useApiRequest(customersApi.updateLogo);

  const updateFile = useCallback(
    (file: FileLink) => {
      request(
        updateRequest,
        [customerId, file.id],
        (_) => {
          toast({
            title: t('fileBrowser.updatedFile'),
            status: 'success',
            isClosable: true,
          });
          setFile(file);
        },
        (error: ServerError) => {
          toast({
            title: t('fileBrowser.updateFileFailed'),
            description: <ErrorsList errors={error.errors} />,
            status: 'error',
            isClosable: true,
          });
        },
      );
    },
    [customerId, setFile, t, toast, updateRequest],
  );

  const {
    fileUploads,
    isUploading,
    uploadFailed,
    uploadFiles,
    removeFileUpload,
  } = useFileUploads(updateFile, maxSize);

  return (
    <Flex direction="column" gap={2} w="100%">
      <Text variant="header-medium">{t('customer.logo')}</Text>
      {error && <ErrorDetails error={error} />}
      {loading && <Spinner />}
      {file && (
        <ImageDropzone
          imageId={file.id}
          imageIdentifier={file.identifier}
          imageSrc={clientEndpoints.previewImage(file.identifier, 250)}
          imageArchived={false}
          allowEdit={true}
          onUpload={(files) => uploadFiles(files, folderId)}
          isUploading={isUploading}
          uploadFailed={uploadFailed}
          fileUploads={fileUploads}
          removeFileUpload={removeFileUpload}
          onRemoveFileOpen={onRemoveFileOpen}
        />
      )}
      {!loading && !file && (
        <FileDropzone
          accept={acceptedImageFormats}
          isUploading={isUploading}
          uploadFailed={uploadFailed}
          fileUploads={fileUploads}
          removeFileUpload={removeFileUpload}
          onUpload={(files) => uploadFiles(files, folderId)}
        />
      )}
      <VerifyDialogWithRequest
        headerTitle={t('fileBrowser.removeFile')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('general.confirm')}
        primaryRequest={customersApi.deleteLogo}
        args={[customerId]}
        isOpen={isRemoveFileOpen}
        onClose={onRemoveFileClose}
        onPerformed={() => setFile(null)}
        onSuccessTitle={t('fileBrowser.fileRemoved')}
      >
        {t('alert.areYouSure')}
      </VerifyDialogWithRequest>
    </Flex>
  );
}
